// Modal "+ Nova conversa" — busca contato com debounce, escolhe loja (multi-store)
// e opcionalmente inicia com um TEMPLATE aprovado (auto-claim). Conversa iniciada
// pela loja só entrega HSM aprovado — por isso aqui não há texto livre, só templates.
// Cria contato inline via ContactCreateModal. SRP: orquestra; sem lógica de domínio direta.
import { useEffect, useMemo, useState } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { PlusIcon, SearchIcon } from '@/components/icons';
import { formatPhone } from '@/lib/utils/formatters';
import {
  ContactCreateModal,
  type Contact,
  useGetContactQuery,
  useListContactsQuery,
} from '@/features/contacts';
import {
  useListStoresQuery,
  useSendableNumbers,
  WhatsappNumberSelect,
} from '@/features/stores';
import { useCreateConversationMutation, useGetStoreTemplatesQuery } from '../api';
import { TemplatePicker } from './TemplatePicker';
import styles from './ConversationCreateModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
  /**
   * Abre já com este contato escolhido ('Iniciar conversa' num cartão ou número recebido
   * no chat). Busca pelo id, igual ao contato recém-criado inline.
   */
  initialContactId?: string;
}

function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function ConversationCreateModal({ open, onClose, onCreated, initialContactId }: Props) {
  const toast = useToast();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [storeId, setStoreId] = useState<string>('');
  // Número por onde a conversa sai. O WhatsappNumberSelect preenche com o principal
  // da loja e se corrige quando a loja muda (senão sairia pelo número de outra loja).
  const [whatsappNumberId, setWhatsappNumberId] = useState<string>('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [pendingCreatedContactId, setPendingCreatedContactId] = useState<string | null>(null);

  // Reseta o form quando o modal fecha — evita lixo entre aberturas.
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedContact(null);
      setStoreId('');
      setWhatsappNumberId('');
      setContactModalOpen(false);
      setPendingCreatedContactId(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && initialContactId) setPendingCreatedContactId(initialContactId);
  }, [open, initialContactId]);

  const { data: contactsPage, isFetching: searching } = useListContactsQuery(
    { q: debouncedSearch || undefined },
    { skip: !open || selectedContact !== null || debouncedSearch.length < 1 },
  );

  // Lojas que o usuário pode usar (back já faz o escopo: GESTOR recebe todas,
  // demais recebem as suas). Fonte única — não depende de currentUser.storeIds.
  const { data: storesPage } = useListStoresQuery(undefined, { skip: !open });
  const availableStores = useMemo(() => storesPage?.items ?? [], [storesPage]);

  // Pré-seleciona a primeira loja disponível assim que a lista chega.
  useEffect(() => {
    if (!storeId && availableStores.length > 0) {
      setStoreId(availableStores[0].id);
    }
  }, [availableStores, storeId]);

  // Auto-seleção do contato recém-criado inline — fetch direto pelo id.
  const { data: createdContact } = useGetContactQuery(
    pendingCreatedContactId ?? '',
    { skip: !pendingCreatedContactId },
  );

  useEffect(() => {
    if (createdContact && pendingCreatedContactId === createdContact.id) {
      setSelectedContact(createdContact);
      setPendingCreatedContactId(null);
      setSearch('');
    }
  }, [createdContact, pendingCreatedContactId]);

  const [createConversation, { isLoading: creating }] =
    useCreateConversationMutation();

  // Mesma query (e mesmo cache) que o WhatsappNumberSelect usa — serve só para decidir
  // se o campo aparece, sem duplicar requisição.
  const { numeros: sendableNumbers } = useSendableNumbers(storeId, !open);

  // Templates aprovados da loja escolhida — só busca com contato + loja definidos.
  const {
    data: templatesData,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useGetStoreTemplatesQuery(storeId, { skip: !open || !storeId || !selectedContact });

  const contactResults = useMemo(
    () => (contactsPage?.items ?? []).slice(0, 8),
    [contactsPage],
  );

  const canSubmit = Boolean(selectedContact && storeId) && !creating;

  const fail = () =>
    toast.danger({
      title: 'Não foi possível criar a conversa',
      description: 'Tente novamente em instantes.',
    });

  // "Criar conversa" simples: entra na fila (QUEUE), sem primeira mensagem.
  const submit = async () => {
    if (!selectedContact || !storeId) return;
    try {
      const conv = await createConversation({
        contactId: selectedContact.id,
        storeId,
        storeWhatsappNumberId: whatsappNumberId || undefined,
      }).unwrap();
      onCreated(conv.id);
      onClose();
    } catch {
      fail();
    }
  };

  // Iniciar já com um template aprovado (auto-claim → ACTIVE). É o caminho padrão de
  // uma conversa cold: a Meta só entrega HSM aprovado fora de uma janela aberta.
  const startWithTemplate = async (
    name: string,
    language: string,
    variables: Record<string, string>,
  ) => {
    if (!selectedContact || !storeId) return;
    try {
      const conv = await createConversation({
        contactId: selectedContact.id,
        storeId,
        storeWhatsappNumberId: whatsappNumberId || undefined,
        template: { name, language, variables },
        clientMessageId: newClientId(),
      }).unwrap();
      onCreated(conv.id);
      onClose();
    } catch {
      fail();
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title="Nova conversa">
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          noValidate
        >
          <div className={styles.fieldset}>
            {selectedContact ? (
              <>
                <span className={styles.label}>Contato</span>
                <div className={styles.selectedContact}>
                  <div className={styles.selectedContactInfo}>
                    <Avatar
                      name={selectedContact.name}
                      tone={selectedContact.avatarTone}
                      size="sm"
                    />
                    <div className={styles.selectedContactText}>
                      <span className={styles.selectedContactName}>
                        {selectedContact.name}
                      </span>
                      <span className={styles.selectedContactPhone}>
                        {formatPhone(selectedContact.phone)}
                      </span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedContact(null)}
                    aria-label="Trocar contato"
                  >
                    Trocar
                  </Button>
                </div>
              </>
            ) : (
              <div className={styles.contactBox}>
                <div className={styles.contactInputRow}>
                  <Input
                    id="conv-contact-search"
                    label="Buscar contato"
                    placeholder="Digite nome ou telefone"
                    leadingIcon={<SearchIcon size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    leadingIcon={<PlusIcon size={14} />}
                    onClick={() => setContactModalOpen(true)}
                  >
                    Criar contato
                  </Button>
                </div>
                {debouncedSearch.length > 0 && (
                  <ul className={styles.results} role="listbox">
                    {searching && contactResults.length === 0 && (
                      <li className={styles.empty}>Buscando…</li>
                    )}
                    {!searching && contactResults.length === 0 && (
                      <li className={styles.empty}>Nenhum contato encontrado.</li>
                    )}
                    {contactResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={false}
                          className={styles.resultItem}
                          onClick={() => {
                            setSelectedContact(c);
                            setSearch('');
                          }}
                        >
                          <Avatar name={c.name} tone={c.avatarTone} size="sm" />
                          <div className={styles.resultText}>
                            <span className={styles.resultName}>{c.name}</span>
                            <span className={styles.resultMeta}>
                              {formatPhone(c.phone)}
                              {c.company ? ` · ${c.company}` : ''}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {availableStores.length > 1 && (
            <div className={styles.fieldset}>
              <label className={styles.label} htmlFor="conv-store">
                Loja
              </label>
              <select
                id="conv-store"
                className={styles.select}
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
              >
                <option value="" disabled>
                  Selecione…
                </option>
                {availableStores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Escolher entre uma opção é ruído: o campo inteiro (rótulo incluso) só
              existe quando a loja tem mais de um número apto a enviar. */}
          {storeId && sendableNumbers.length > 1 && (
            <div className={styles.fieldset}>
              <label className={styles.label} htmlFor="conv-whatsapp-number">
                Enviar pelo número
              </label>
              <WhatsappNumberSelect
                id="conv-whatsapp-number"
                storeId={storeId}
                value={whatsappNumberId}
                onChange={setWhatsappNumberId}
              />
            </div>
          )}

          {selectedContact && storeId && (
            <div className={styles.fieldset}>
              <span className={styles.label}>Iniciar com um template (opcional)</span>
              <p className={styles.templateHint}>
                Conversas iniciadas pela loja só entregam templates aprovados pela Meta.
                Escolha um para começar o atendimento agora, ou crie na fila.
              </p>
              <TemplatePicker
                data={templatesData}
                isLoading={templatesLoading}
                isError={templatesError}
                onSend={startWithTemplate}
              />
            </div>
          )}

          <div className={styles.actions}>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={creating}
              disabled={!canSubmit}
            >
              Criar na fila
            </Button>
          </div>
        </form>
      </Modal>

      <ContactCreateModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onCreated={(id) => {
          // Triggera fetch por id; useEffect acima auto-seleciona quando chega.
          setPendingCreatedContactId(id);
          setContactModalOpen(false);
        }}
      />
    </>
  );
}
